// Copyright (c) Bentley Systems
import * as React from "react";
import { Input } from "@itwin/itwinui-react";
import Action from "../../Action/Action";
import { ActionParameterVm } from "../../Action/action-parameter-vm";
import { ActionVm } from "../../Action/action-vm";

type EtreeToolbarProps = {
  headerActions: ActionVm[];
  searchQuery: string;
  searchValidationError: string | null;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  setSearchValidationError: React.Dispatch<React.SetStateAction<string | null>>;
  isValidEtreeSearchTerm: (value: string) => boolean;
  appliedSearchTermRef: React.MutableRefObject<string>;
  setAppliedSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  restoreExpandedNodes: (expandedNodesToRestore: Record<string, boolean>) => void;
  restoreExpansionIntent: (
    policy: Record<string, boolean>,
    manual: Record<string, boolean>,
    collapsed: Record<string, boolean>
  ) => void;
  refreshTree: (searchTermOverride?: string, reason?: "search" | "action" | "manual" | "expansion" | "panel-close") => Promise<void>;
  resolveActionParametersFromUrl: (sourceParams: ActionParameterVm[]) => ActionParameterVm[];
  navigateToInfPanelFunction: (navFrolUrl: string) => void;
};

const EtreeToolbar = ({
  headerActions,
  searchQuery,
  searchValidationError,
  setSearchQuery,
  setSearchValidationError,
  isValidEtreeSearchTerm,
  appliedSearchTermRef,
  setAppliedSearchTerm,
  restoreExpandedNodes,
  restoreExpansionIntent,
  refreshTree,
  resolveActionParametersFromUrl,
  navigateToInfPanelFunction,
}: EtreeToolbarProps) => {
  return (
    <div className="becs-ltr-margin">
      <div
        className="horizontal becs-tb-margin"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div className="leftAlign" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="rowWrap">
            {headerActions.map((action) => {
              const processedAction = new ActionVm(action);
              processedAction.actionParameters = resolveActionParametersFromUrl(
                action.actionParameters
              );
              return (
                <Action
                  action={processedAction}
                  key={action.actionId}
                  actionEndedFunction={async () => refreshTree(undefined, "action")}
                  navigateToInformationPanelFunction={navigateToInfPanelFunction}
                />
              );
            })}
          </div>
        </div>
        <div className="rightAlign">
          <div style={{ width: "100%", maxWidth: "320px" }}>
            <Input
              type="search"
              value={searchQuery}
              onChange={(event) => {
                const nextSearchQuery = event.target.value;
                setSearchQuery(nextSearchQuery);

                const trimmedNextSearchQuery = nextSearchQuery.trim();
                if (!trimmedNextSearchQuery) {
                  setSearchValidationError(null);
                  if (appliedSearchTermRef.current.trim()) {
                    appliedSearchTermRef.current = "";
                    setAppliedSearchTerm("");
                    restoreExpandedNodes({});
                    restoreExpansionIntent({}, {}, {});
                    void refreshTree("", "manual");
                  }
                  return;
                }

                if (isValidEtreeSearchTerm(trimmedNextSearchQuery)) {
                  setSearchValidationError(null);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  const termToApply = searchQuery.trim();

                  if (!termToApply) {
                    setSearchValidationError(null);
                    appliedSearchTermRef.current = "";
                    setAppliedSearchTerm("");
                    restoreExpandedNodes({});
                    restoreExpansionIntent({}, {}, {});
                    void refreshTree("", "manual");
                    return;
                  }

                  if (!isValidEtreeSearchTerm(termToApply)) {
                    setSearchValidationError(
                      "Use 3+ chars: letters, numbers, underscore, hyphen, spaces between words."
                    );
                    return;
                  }

                  setSearchValidationError(null);
                  appliedSearchTermRef.current = termToApply;
                  setAppliedSearchTerm(termToApply);
                  restoreExpandedNodes({});
                  restoreExpansionIntent({}, {}, {});
                  void refreshTree(termToApply, "search");
                }
              }}
              placeholder="Search (min 3 chars)"
              aria-label="Search tree"
              size="small"
              style={{
                width: "100%",
                maxWidth: "320px",
                height: "36px",
                outline: "none",
                borderColor: searchValidationError ? "var(--iui-color-border-negative)" : undefined,
              }}
            />
            {searchValidationError && (
              <div
                style={{
                  color: "var(--iui-color-text-negative)",
                  fontSize: "12px",
                  marginTop: "4px",
                  lineHeight: 1.2,
                }}
              >
                {searchValidationError}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EtreeToolbar;
